import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';
import type { Recipe, Formula } from '../queries/recipe.queries';

interface CreateRecipeInput {
  menuItemId: string;
  formulas: Formula[];
  defaultFormulaId: 'A' | 'B' | 'C' | 'D';
}

interface UpdateRecipeInput {
  formulas?: Formula[];
  defaultFormulaId?: 'A' | 'B' | 'C' | 'D';
}

// Create new recipe
export function useCreateRecipe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRecipeInput) => {
      const response = await apiClient.post<APIResponse<{ recipe: Recipe }>>(
        '/recipes',
        data
      );
      return response.data.data.recipe;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });
}

// Update recipe
export function useUpdateRecipe(recipeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateRecipeInput) => {
      const response = await apiClient.patch<APIResponse<{ recipe: Recipe }>>(
        `/recipes/${recipeId}`,
        data
      );
      return response.data.data.recipe;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
    },
  });
}

// Submit recipe for review
export function useSubmitRecipeForReview(recipeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<APIResponse<{ recipe: Recipe }>>(
        `/recipes/${recipeId}/submit-review`
      );
      return response.data.data.recipe;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
    },
  });
}

// Approve/Publish recipe (move to published)
export function useApproveRecipe(recipeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<APIResponse<{ recipe: Recipe }>>(
        `/recipes/${recipeId}/publish`,
        {}
      );
      return response.data.data.recipe;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
      queryClient.invalidateQueries({ queryKey: ['published-recipe'] });
    },
  });
}

// Reject recipe
export function useRejectRecipe(recipeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reason: string) => {
      const response = await apiClient.patch<APIResponse<{ recipe: Recipe }>>(
        `/recipes/${recipeId}/reject`,
        { reason }
      );
      return response.data.data.recipe;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
    },
  });
}

// Archive recipe
export function useArchiveRecipe(recipeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<APIResponse<{ recipe: Recipe }>>(
        `/recipes/${recipeId}/archive`
      );
      return response.data.data.recipe;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
    },
  });
}

// Clone recipe to create new draft version
export function useCloneRecipe(recipeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { version: string; notes?: string }) => {
      const response = await apiClient.post<APIResponse<{ recipe: Recipe }>>(
        `/recipes/${recipeId}/clone`,
        data
      );
      return response.data.data.recipe;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });
}

// Toggle formula active status
export function useToggleFormulaStatus(recipeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { formulaId: string; isActive: boolean }) => {
      const response = await apiClient.patch<APIResponse<{ recipe: Recipe }>>(
        `/recipes/${recipeId}/formula/${data.formulaId}/toggle`,
        { isActive: data.isActive }
      );
      return response.data.data.recipe;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
    },
  });
}

export type { CreateRecipeInput, UpdateRecipeInput };
